import api from "@/api";
import { sendUserJourney } from "@/api/userJourney";
import Button from "@/components/Button";
import Column from "@/components/Column";
import Columns from "@/components/Columns";
import Container from "@/components/Container";
import ErrorText from "@/components/ErrorText";
import Footer from "@/components/Footer";
import ModalCustomerValidation from "@/components/ModalCustomerValidation";
import SatisfyCart from "@/components/SatisfactionSurvey/SatisfyCart";
import SEO from "@/components/SEO";
import Text from "@/components/Text";
import Title from "@/components/Title";
import TopBar from "@/components/TopBar";
import useBreakpoint from "@/hooks/useBreakpoint";
import useTabsValidation from "@/hooks/useTabsValidation";
import useVerifySMSConfirmationIsOn from "@/hooks/useVerifySMSConfimationIsOn/useVerifySMSConfimationIsOn";
import { useFormStore } from "@/store/form";
import { useModalStore } from "@/store/modal";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers } from "@/utils";
import { downloadPdfFile } from "@/utils/blob";
import { formatPrice } from "@/utils/currency";
import {
  removeMarketingInfo,
  removeUuidStorageSession,
} from "@/utils/saleSessionStorage";
import {
  formatFirstName,
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useTagManager } from "@/hooks/useTagManager";

const pageConfig = {
  route: "/confirmation/auto-debt",
  prevRoute: "/short-flow-finish-order",
};

const BannerLive = () => {
  return (
    <div className="mt-4 cursor-pointer">
      <a href="https://timfibra-assine.com.br/?utm_source=omega&utm_medium=ecommerce&utm_campaign=crossselling">
        <img
          className="rounded-xl"
          alt="assine ultrafibra 500mb"
          src="/assine-ultrafibra-500mb.png"
        />
      </a>
    </div>
  );
};

const BannerAmpli = () => {
  const { isOnBreakpoint } = useBreakpoint(1024);

  const ampliLink = import.meta.env.VITE_AMPLI_LINK;
  const linkProps = {
    href: ampliLink,
    target: "_blank",
  };

  return (
    <>
      {isOnBreakpoint ? (
        <Title className="mt-2">
          <a {...linkProps} rel="noreferrer">
            <img
              src="/ampli_card_mobile.svg"
              alt="App beneficios"
              style={{ width: "100%" }}
              data-ic-item="sec-banner-finalização"
            />
          </a>
        </Title>
      ) : (
        <Title className="mt-2">
          <a {...linkProps} rel="noreferrer">
            <img
              src="/ampli_card.svg"
              alt="App beneficios"
              style={{ width: "100%" }}
              data-ic-item="sec-banner-finalização"
            />
          </a>
        </Title>
      )}
    </>
  );
};

const Media = () => {
  const linkLP = import.meta.env.VITE_LP_LINK;

  const textShare = import.meta.env.VITE_SOCIAL_MEDIA_SHARING_TEXT;

  const linkProps = {
    whatsapp: {
      href: `https://api.whatsapp.com/send?text=${textShare} ${linkLP}`,
      target: "_blank",
    },
    facebook: {
      href: `https://www.facebook.com/sharer/sharer.php?u=${linkLP}`,
      target: "_blank",
    },
    twitter: {
      href: `https://twitter.com/intent/tweet?url=${linkLP}&text=${textShare}`,
      target: "_blank",
    },
  };
  return (
    <Title className="flex ">
      <div
        className="mx-1 rounded border"
        style={{ color: "rgba(0, 0, 0, 0,15)" }}
      >
        <a {...linkProps.whatsapp} rel="noreferrer">
          <img src="/whatsapp.svg" alt="Whatsapp" className="px-2 py-2" />
        </a>
      </div>
      <div
        className="mx-1 rounded border"
        style={{ color: "rgba(0, 0, 0, 0,15)" }}
      >
        <a {...linkProps.facebook} rel="noreferrer">
          <img src="/facebook.svg" alt="Facebook" className="px-2 py-2" />
        </a>
      </div>
      <div
        className="mx-1 rounded border"
        style={{ color: "rgba(0, 0, 0, 0,15)" }}
      >
        <a {...linkProps.twitter} rel="noreferrer">
          <img src="/twitter.svg" alt="Twitter" className="px-2 py-2" />
        </a>
      </div>
    </Title>
  );
};

export const ConfirmationNewLine = () => {
  const { dataLayer } = useTagManager();
  useTabsValidation(pageConfig.route, pageConfig.prevRoute);
  const showBannerLive = import.meta.env.VITE_SHOW_BANNEER_LIVE === "true";
  const showBannerAmpli = import.meta.env.VITE_SHOW_BANNEER_AMPLI === "true";
  const bannerAmpliRef = useRef<HTMLDivElement>(null);

  const initialized = useRef(false);

  const cpf = useFormStore((store) => store.value.cpf);
  const tel = useFormStore((store) => store.value.tel);
  const saleId = useFormStore((store) => store.saleId);
  const name = useFormStore((store) => store.value.fullName);
  const utmSource = useFormStore((store) => store.value.utm_source);
  const email = useFormStore((store) => store.value.email);
  const isPortability = useFormStore((store) => store.isPortability);
  const ddd = useFormStore((store) => store.value.tel).slice(0, 2);
  const smsConfirmationIsOn = useVerifySMSConfirmationIsOn(
    ddd,
    isPortability()
  );

  const showModalCustomerValidation = useModalStore(
    (store) => store.showModalCustomerValidation
  );

  const dueDate = useFormStore((store) => {
    const { payment } = store.value;
    return payment?.debtDueDate;
  });
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(tel).slice(0, 2))
  );
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());

  const price = `R$${formatPrice(currentPrice)}`;

  const form = useFormStore((store) => store.value);
  const productStore = useProductStore((store) => store.item);
  const loyalty = useProductStore((store) => store.loyalty);

  const [receiptDownloadError, setError] = useState(false);

  const title =
    productStore?.segmento === "POS_PAGO" ||
    productStore?.segmento === "POS_EXPRESS"
      ? "TIM Black | Confirmação"
      : "TIM Controle | Confirmação";

  const handleReceiptDownload = (saleID: number | null, tel: string) => {
    const telFormat = getOnlyNumbers(tel);
    const params = {
      gb: productStore?.gb,
    };
    return api
      .get(`/core/pdf/${saleID}/gerar-comprovante/${telFormat}`, {
        params,
        responseType: "blob",
      })
      .then((response) => {
        setError(false);
        const file = response.data;
        const filename = `comprovante-${saleID}`;
        downloadPdfFile({ filename, file });
      })
      .catch(() => {
        setError(true);
      });
  };

  useEffect(() => {
    if (!initialized.current && saleId !== null) {
      initialized.current = true;

      removeMarketingInfo();
      removeUuidStorageSession();
      Cookies.remove("offer");
      sendUserJourney({
        ...form,
        product: productStore!,
        loyalty,
        ...{
          passo: "CONCLUIR_VENDA",
        },
      });
      dataLayer({
        event: "conversao",
        sku: removeAccentsAndSetUpperCase(productStore?.tim_id),
        nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
          productStore?.nome
        )}-${totalGbProduct}`,
        valor: formatPrice(currentPrice),
        status: "Sucesso",
        pedido: saleId,
      });
    }
  }, [
    form,
    productStore,
    loyalty,
    currentPrice,
    saleId,
    totalGbProduct,
    dataLayer,
  ]);

  // Page
  return (
    <div className="min-h-screen bg-[#f4f6f9]">
      <SEO
        title={title}
        description="Parabéns seu plano TIM foi solicitado com sucesso!"
      />
      <TopBar />

      <Columns
        className="mx-auto flex max-w-lg flex-1 flex-col items-center justify-center px-4 py-6"
        useNegativeMargin={false}
      >
        {utmSource === "najiba" && saleId && (
          <img
            src={`https://xads.go2cloud.org/aff_goal?a=l&goal_name=fechamento-plano&adv_id=768&offer_id=851&adv_sub=${saleId}`}
            width="1"
            height="1"
            alt=""
          />
        )}
        <Column size={12}>
          <Container
            as="main"
            className="box-border flex w-auto max-w-full flex-col items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm"
          >
            <Title
              kind="blue"
              className="w-full rounded-t-lg p-6 text-center"
              dataIcTitle="sec-omega-comunicacao"
            >
              <Text as="h2" className="pb-3 font-bold leading-none" size="5xl">
                {name ? `Parabéns, ${formatFirstName(name)}!` : "Parabéns!"}
              </Text>
              <Text as="span" className="font-normal leading-none" size="2xl">
                Seu pedido foi recebido!
              </Text>
              {isPortability() && !smsConfirmationIsOn && (
                <Text
                  as="span"
                  className="ml-8 mr-6 mt-4 block font-normal leading-5"
                  size="base"
                >
                  <strong>ATENÇÃO:</strong> para a sua segurança, você receberá
                  um <strong>SMS do numero 7678</strong> perguntando se você
                  quer realizar a portabilidade - que é como chamamos esse
                  processo de levar o número de uma operadora para outra - no
                  número da sua operadora atual. <br />
                  <strong>
                    Você deve responder SIM em um prazo de até 24 horas
                  </strong>
                  . <br />
                  Lembre-se de colocar o chip da TIM em seu aparelho{" "}
                  <strong>
                    somente depois de responder a mensagem acima
                  </strong>{" "}
                  .
                </Text>
              )}
            </Title>

            <Title
              kind="white"
              className="w-full rounded-b-lg p-5 pb-10 text-center"
            >
              <Text
                as="h1"
                className="mb-5 mt-4 block  font-normal leading-none"
                size="lg"
                kind="indigo"
              >
                {productStore?.nome}
              </Text>
              <Text
                as="span"
                className="mt-2 font-normal leading-none"
                size="2xl"
              >
                <strong className="font-bold">{productStore?.gb}GB</strong>
                <Text
                  as="span"
                  className="mt-2 font-normal leading-none"
                  size="2xl"
                  kind="indigo"
                >
                  {" "}
                  por {price}/mês
                </Text>
              </Text>
              {loyalty && form.paymentType?.value !== "Cartão de Crédito" && (
                <Text
                  as="span"
                  className="mt-1 block font-normal leading-none"
                  size="1xl"
                  kind="gray"
                >
                  com fidelidade de 12 meses
                </Text>
              )}

              {dueDate && (
                <Text
                  as="span"
                  className="mt-8 block font-normal leading-none"
                  size="xl"
                >
                  Vence todo <strong>dia {dueDate} </strong> de cada mês
                </Text>
              )}
              <Text
                as="span"
                className="mt-1 block font-normal leading-none"
                size="xl"
              >
                A fatura será enviada <strong>por e-mail</strong>
              </Text>

              {name && (
                <>
                  <Text
                    as="span"
                    className="mt-4 block font-normal leading-none"
                    size="base"
                    kind="gray"
                  >
                    Cliente:
                  </Text>
                  <Text
                    as="span"
                    className="mt-1 block font-bold leading-none"
                    size="2xl"
                    data-openreplay-obscured
                  >
                    {name}
                  </Text>
                </>
              )}

              <Text
                as="span"
                className="mt-4 block font-normal leading-none"
                size="base"
                kind="gray"
              >
                CPF:
              </Text>
              <Text
                as="span"
                className="mt-1 block font-bold leading-none"
                size="2xl"
                data-openreplay-obscured
              >
                {cpf}
              </Text>
              <Text
                as="span"
                className="mt-4 block font-normal leading-none"
                size="base"
                kind="gray"
              >
                Código do pedido
              </Text>
              <Text
                as="span"
                className="mt-1 block font-bold leading-none"
                size="2xl"
                kind="blue"
              >
                {saleId}
              </Text>
              {email && (
                <Text
                  as="span"
                  className="mt-4 block font-normal leading-none"
                  size="base"
                  kind="gray"
                >
                  Iremos entrar em contato via email: <strong>{email}</strong>{" "}
                  para avisar sobre as atualizações do seu pedido
                </Text>
              )}
            </Title>

            <Column size={[11]} className="mx-auto pb-8 text-center">
              <div
                className="pb-1 border-t border-gray-200"
              />
              <Button
                type="button"
                className="mt-6 text-base font-bold"
                large
                kind="default"
                onClick={() => {
                  handleReceiptDownload(saleId, tel);
                }}
                data-ic-trigger="sec-omega-comprovante"
              >
                VER COMPROVANTE
              </Button>

              {receiptDownloadError ? (
                <Column size={12} className="pb-6 pt-4 text-center">
                  <ErrorText id="process-error">
                    Ocorreu um erro ao fazer o download do comprovante.
                  </ErrorText>
                </Column>
              ) : null}

              <Column className="flex items-center justify-center pb-4 pt-4">
                <Text
                  as="span"
                  className="mr-2 mt-1 block font-normal leading-none"
                  size="1xl"
                  kind="gray"
                >
                  Compartilhar
                </Text>
                <Media />
              </Column>
            </Column>
          </Container>
        </Column>

        <Column size={12}>
          <SatisfyCart saleId={saleId} />
        </Column>

        {showBannerLive && (
          <Column size={12}>
            <BannerLive />
          </Column>
        )}
        {showBannerAmpli && (
          <Column size={12} className="py-4">
            <div ref={bannerAmpliRef}>
              <BannerAmpli />
            </div>
          </Column>
        )}
      </Columns>
      <Column className="mb-4 mt-8">
        <Footer showText />
      </Column>

      {showModalCustomerValidation && <ModalCustomerValidation />}
    </div>
  );
};

export default ConfirmationNewLine;
